import React, { memo, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { darken, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { checkIsLightTheme } from 'utils/theme';

import { loanManagementTestIds } from '../../loanManagement.testIds';

import { DEFAULT_BOTTOM_MARGIN } from './Wrapper.constants';

const getStyles = ({ palette, breakpoints }, { removeBottomMargin }) => {
  const isLightTheme = checkIsLightTheme(palette.type);

  return {
    root: {
      width: '100%',
      border: isLightTheme ? null : `1px solid ${palette.primary['300']}`,
      backgroundColor: isLightTheme ? null : palette.background.paper,
      borderRadius: '10px',
      overflow: 'hidden',
      marginBottom: removeBottomMargin ? null : `${DEFAULT_BOTTOM_MARGIN}px`,
    },
    header: {
      padding: isLightTheme ? '20px 5px' : '20px 30px',
      color: isLightTheme ? palette.common.black : null,

      [breakpoints.down('sm')]: {
        padding: '10px 20px',
      },

      [breakpoints.down('xs')]: {
        padding: '10px',
      },
    },
    content: {
      backgroundColor: isLightTheme ? palette.common.white : null,
      borderRadius: '20px',
      padding: '30px',

      '& > div:not(:last-child)': {
        marginBottom: '30px',
      },

      [breakpoints.down('sm')]: {
        padding: '10px 20px',
      },

      [breakpoints.down('xs')]: {
        padding: '10px',
      },
    },
    buttonWrapper: {
      '& .Mui-disabled': {
        color: isLightTheme ? darken(palette.common.white, palette.action.disabledOpacity) : null,
      },
    },
  };
};

const Wrapper = ({
  children,
  config,
  isButtonsVisible,
  dataTestId,
  removeBottomMargin = false,
}) => {
  const theme = useTheme();
  const classes = useMemo(
    () => getStyles(theme, { removeBottomMargin }),
    [theme, removeBottomMargin],
  );
  const { name, methods = {} } = config;

  return (
    <Box data-testid={dataTestId} sx={classes.root}>
      <Box className="flex justify-between" sx={classes.header}>
        <div className="flex items-center">
          <Typography color="inherit" variant="bold16">
            {name}
          </Typography>
        </div>
        {isButtonsVisible && (
          <div className="flex">
            {Object.values(methods).map(({ icon: IconComponent, name, ...props }, index) => (
              <Box className="flex items-center pr-6" key={index} sx={classes.buttonWrapper}>
                <Button
                  {...props}
                  color="primary"
                  data-testid={loanManagementTestIds.getWrapperActionButtonId(index)}
                  variant="contained"
                >
                  <IconComponent className="mr-6" size={IconSize[20]} />
                  {name}
                </Button>
              </Box>
            ))}
          </div>
        )}
      </Box>

      <Box sx={classes.content}>{children}</Box>
    </Box>
  );
};

Wrapper.propTypes = {
  config: PropTypes.object.isRequired,
  isButtonsVisible: PropTypes.bool.isRequired,
  dataTestId: PropTypes.string.isRequired,
  removeBottomMargin: PropTypes.bool,
};

export default memo(Wrapper);
